import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { FormField } from "@/components/ui/FormField";
import { Input } from "@/components/ui/Input";
import { fetchJson, ApiError } from "@/lib/api";

import { AuthShell } from "../shell/AuthShell";

// Phase 2a customer-accounts FR-5 — consume a password-reset token and
// set a new password. The token comes via ?token= in the URL the user
// clicked from their email. We deliberately render a generic error on
// failure (no distinguishing between "invalid token" and "weak
// password") so callers can't probe the token via a weak password.
export function PasswordEditPage(): JSX.Element {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") ?? "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    if (!token) {
      setError("This link is missing its reset token. Request a new email.");
      return;
    }
    setPending(true);
    try {
      await fetchJson("/api/v1/auth/password", {
        method: "PUT",
        body: { reset_password_token: token, password, password_confirmation: confirm }
      });
      navigate("/portal/profile#security", { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        setError("That link is invalid or has expired, or the password is too weak.");
      } else {
        setError("Something went wrong. Try again.");
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <AuthShell
      eyebrow="Account"
      title="Choose a new password"
      description="At least 10 characters, with a letter and a number."
      footer={
        <Link to="/sign-in" className="font-semibold text-brand-white underline-offset-2 hover:underline">
          ← Back to sign in
        </Link>
      }
    >
      <form onSubmit={onSubmit} noValidate className="space-y-4">
        {error ? (
          <Alert variant="destructive" role="alert">
            {error}
          </Alert>
        ) : null}
        <FormField id="password" label="New password" required>
          <Input id="password" type="password" autoComplete="new-password" value={password}
                 onChange={(e) => setPassword(e.target.value)} required />
        </FormField>
        <FormField id="confirm" label="Confirm new password" required>
          <Input id="confirm" type="password" autoComplete="new-password" value={confirm}
                 onChange={(e) => setConfirm(e.target.value)} required />
        </FormField>
        <Button type="submit" disabled={pending} className="w-full" size="lg">
          {pending ? "Saving…" : "Set new password"}
        </Button>
      </form>
    </AuthShell>
  );
}
